import { Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { setFromValueParamOnlyTruthy } from "lib/setFromEvent"
import { getTargetValue } from "./share-link-comment-feed-helpers"

export function ShareLinkCommentToolbar({ comments }) {
    const theme = useTheme()
    const [filter, setFilter] = useSearchParam("commentFilter", "open")
    const [target, setTarget] = useSearchParam("commentTarget", "all")
    const targetOptions = getTargetOptions(comments)

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-cy="share-link-comments-toolbar"
            sx={{ flexWrap: "wrap", py: 0.5 }}
        >
            <ToggleButtonGroup
                exclusive
                size="small"
                value={filter}
                onChange={setFromValueParamOnlyTruthy(setFilter)}
                data-cy="share-link-comments-filter"
                sx={getShareLinkCommentToolbarToggleSx(theme)}
            >
                <ToggleButton value="open">Open</ToggleButton>
                <ToggleButton value="resolved">Resolved</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
            <StandardSelector
                value={target}
                onChange={setTarget}
                options={targetOptions}
                data-cy="share-link-comments-target"
                sx={getShareLinkCommentToolbarSelectorSx(theme)}
                optionSx={getShareLinkCommentToolbarOptionSx(theme)}
            />
        </Stack>
    )
}

export function getShareLinkCommentToolbarOptionSx(theme) {
    return {
        py: 0.75,
        fontSize: theme.typography.caption.fontSize,
    }
}

export function getShareLinkCommentToolbarSelectorSx(theme) {
    return {
        px: 1,
        py: 0.25,
        minHeight: 24,
        fontSize: theme.typography.caption.fontSize,
        lineHeight: 1.6,
        textTransform: "none",
        "& .MuiButton-endIcon": { ml: 0.25 },
    }
}

export function getShareLinkCommentToolbarToggleSx(theme) {
    return {
        "& .MuiToggleButton-root": {
            px: 1,
            py: 0.25,
            fontSize: theme.typography.caption.fontSize,
            lineHeight: 1.6,
            textTransform: "none",
        },
    }
}

// Exported for direct unit testing. Build the target dropdown options from the distinct anchors
// present in the comment threads.
export function getTargetOptions(comments) {
    const options = [{ value: "all", label: "All targets" }]
    const seen = new Set(["all"])
    for (const comment of comments ?? []) {
        const option = optionForAnchor(comment?.anchor)
        if (!option || seen.has(option.value)) continue
        seen.add(option.value)
        options.push(option)
    }
    return options
}

function optionForAnchor(anchor) {
    if (!anchor) return null
    // Shared with the feed filter (`getTargetValue`) so toolbar option values and feed filtering
    // stay in sync.
    const value = getTargetValue(anchor)
    if (!value) return null
    return { value, label: anchor.targetLabel || defaultLabelForAnchor(anchor) }
}

function defaultLabelForAnchor(anchor) {
    if (anchor.type === "schedule") return "Schedule"
    if (anchor.type === "intro") return "Introduction"
    if (anchor.type === "task") return `Task ${Number(anchor.taskIndex) + 1}`
    return "Target"
}
